import React, { useState, useEffect } from "react";
import CarCard from '../CarCard/CarCard'; 
import { useCars } from '../../context/CarContext';
import { FaSearch, FaFilter, FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";
import "./CarList.css";

const CarList = () => {
  const { cars } = useCars();
  const [filteredCars, setFilteredCars] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 10000 }); 
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Déterminer les catégories uniques et la plage de prix
  const categories = ['all', ...new Set(cars.map(car => car.category).filter(Boolean))];
  const maxPrice = Math.max(...cars.map(car => car.price), 0);
  
  useEffect(() => {
    // Initialiser la plage de prix maximale en fonction des voitures disponibles
    if (maxPrice > priceRange.max) {
      setPriceRange(prev => ({ ...prev, max: maxPrice + 1000 })); 
    }
  }, [maxPrice, priceRange.max]);

  useEffect(() => {
    // Filtrer les voitures en fonction des critères
    let results = [...cars];
    
    // Filtre par terme de recherche
    if (searchTerm) {
      results = results.filter(car => 
        car.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (car.description && car.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filtre par catégorie
    if (selectedCategory !== 'all') {
      results = results.filter(car => car.category === selectedCategory);
    }
    
    // Filtre par plage de prix
    results = results.filter(car => 
      car.price >= priceRange.min && car.price <= priceRange.max
    );
    
    // Tri par prix
    results.sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.price - b.price;
      } else {
        return b.price - a.price;
      }
    });
    
    setFilteredCars(results);
  }, [cars, searchTerm, priceRange, sortOrder, selectedCategory]);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handlePriceChange = (e) => {
    setPriceRange({
      ...priceRange,
      [e.target.name]: parseInt(e.target.value)
    });
  };
  
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };
  
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  return (
    <section className="car-list-section">
      <div className="container">
        <h2 className="section-title">Nos voitures disponibles</h2>
        
        {/* Barre de recherche et filtres cachés */}
        <div className="filter-container" style={{ display: 'none' }}>
          <div className="search-box">
            <input 
              type="text" 
              placeholder="Rechercher une voiture..." 
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <FaSearch className="search-icon" />
          </div>
          
          <div className="filters">
            <div className="filter-group">
              <label><FaFilter /> Catégorie</label>
              <select value={selectedCategory} onChange={handleCategoryChange}>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'Toutes les catégories' : category}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Prix min: {priceRange.min} DZD</label>
              <input 
                type="range" 
                name="min" 
                min="0" 
                max={maxPrice} 
                value={priceRange.min} 
                onChange={handlePriceChange}
              />
            </div>
            
            <div className="filter-group">
              <label>Prix max: {priceRange.max} DZD</label>
              <input 
                type="range" 
                name="max" 
                min="0" 
                max={maxPrice} 
                value={priceRange.max} 
                onChange={handlePriceChange}
              />
            </div>
            
            <button className="sort-button" onClick={toggleSortOrder}>
              {sortOrder === 'asc' 
                ? <><FaSortAmountUp /> Prix croissant</> 
                : <><FaSortAmountDown /> Prix décroissant</>
              }
            </button>
          </div>
        </div>
        
        <div className="results-info">
          <p>{filteredCars.length} voiture(s) trouvée(s)</p>
        </div>
        
        <div className="car-list-container">
          {filteredCars.length > 0 ? (
            filteredCars.map((car) => (
              <CarCard key={car.id} car={car} />
            ))
          ) : (
            <div className="no-results">
              <p>Aucune voiture ne correspond à vos critères de recherche.</p>
              <button onClick={() => {
                setSearchTerm('');
                setPriceRange({ min: 0, max: maxPrice });
                setSelectedCategory('all');
              }}>Réinitialiser les filtres</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CarList;
